import { useEffect, useState } from "react";
import { api, errText } from "../lib/api";
import { toast } from "sonner";
import { Check, Flame, Timer, Zap } from "lucide-react";

// Mirror of backend services/playbook.py PLAYBOOK_STEPS.
export const PLAYBOOK_STEPS = [
  { key: "like_save", title: "Like & save your own post", detail: "Gives your content an immediate engagement signal." },
  { key: "share_story", title: "Share it to your Stories", detail: "More views, more watch time, and another chance for your audience to engage." },
  { key: "comments_cta", title: "Leave three comments on your post", detail: "Include your keyword CTA, ask a question, and pin your CTA comment to the top.", auto: true },
  { key: "active_10", title: "Be active for the next 10 minutes", detail: "Reply to DMs, answer comments, and engage with other creators." },
  { key: "respond_fast", title: "Respond to every new comment quickly", detail: "Keep the conversation going with follow-up questions." },
  { key: "pin_keyword", title: "Pin comments with your keyword", detail: "Creates social proof and encourages more people to participate." },
];

const GOLDEN_WINDOW = 600; // seconds

const fmt = (s) => {
  const m = Math.floor(Math.abs(s) / 60);
  const sec = Math.abs(s) % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export const EngagementPlaybook = ({ post, onUpdate, compact = false }) => {
  const [busy, setBusy] = useState(null);
  const startIso = post.published_at || post.scheduled_at;
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!startIso) { setRemaining(null); return; }
    const tick = () => {
      const elapsed = Math.floor((Date.now() - new Date(startIso).getTime()) / 1000);
      setRemaining(GOLDEN_WINDOW - elapsed);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [startIso]);

  const pb = post.playbook || {};
  const isDone = (k) => (k === "comments_cta" && post.first_comment_posted) || !!pb[k]?.done;
  const doneCount = PLAYBOOK_STEPS.filter((s) => isDone(s.key)).length;
  const windowOpen = remaining !== null && remaining > 0;

  const toggle = async (key) => {
    if (key === "comments_cta" && post.first_comment_posted) return; // auto-locked
    const next = !isDone(key);
    setBusy(key);
    try {
      const { data } = await api.put(`/posts/${post.id}/playbook`, { key, done: next });
      onUpdate && onUpdate(data);
    } catch (e) {
      toast.error(errText(e), { duration: Infinity, closeButton: true });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid={`engagement-playbook-${post.id}`}
      className="rounded-xl border border-accent/30 bg-accent/5 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-accent/10 border-b border-accent/20">
        <div className="flex items-center gap-2 min-w-0">
          <Flame size={15} className="text-accent shrink-0" />
          <p className="text-xs font-semibold truncate">Engagement Playbook · the golden 10 minutes</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] font-mono text-muted-foreground" data-testid={`playbook-progress-${post.id}`}>
            {doneCount}/{PLAYBOOK_STEPS.length}
          </span>
          {remaining !== null && (
            <span data-testid={`playbook-timer-${post.id}`}
              className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded flex items-center gap-1 ${
                windowOpen ? "bg-accent text-accent-foreground animate-pulse" : "bg-muted text-muted-foreground"}`}>
              <Timer size={10} />
              {windowOpen ? fmt(remaining) : "window closed"}
            </span>
          )}
        </div>
      </div>
      <ul className="divide-y divide-border/60">
        {PLAYBOOK_STEPS.map((s, i) => {
          const done = isDone(s.key);
          const auto = s.auto && post.first_comment_posted;
          return (
            <li key={s.key}>
              <button
                type="button"
                data-testid={`playbook-step-${s.key}-${post.id}`}
                disabled={busy === s.key || auto}
                onClick={() => toggle(s.key)}
                className={`w-full flex items-start gap-2.5 text-left px-3 py-2 transition-colors ${
                  auto ? "cursor-default" : "hover:bg-accent/5"}`}>
                <span className={`mt-0.5 w-4 h-4 rounded-[5px] border flex items-center justify-center shrink-0 transition-colors ${
                  done ? "bg-accent border-accent text-accent-foreground" : "border-muted-foreground/40"}`}>
                  {done && <Check size={11} strokeWidth={3} />}
                </span>
                <span className="min-w-0">
                  <span className={`text-xs font-medium flex items-center gap-1.5 ${done ? "line-through text-muted-foreground" : ""}`}>
                    <span className="text-muted-foreground font-mono">{i + 1}.</span> {s.title}
                    {auto && <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wide text-accent"><Zap size={9} /> auto</span>}
                  </span>
                  {!compact && <span className="block text-[11px] text-muted-foreground mt-0.5">{s.detail}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default EngagementPlaybook;
